import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../components/headerSection/navbar/Navbar";
import FormInput from "../../components/formInput/FormInput";
import { useTranslation } from "react-i18next";
import resetPasswordService from "../../services/resetPasswordService";

function ResetPassword() {
  const [passwordData, setPasswordData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isValidToken, setIsValidToken] = useState(null); // null = checking, true = valid, false = invalid
  const [token, setToken] = useState("");

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get("token");

    if (!resetToken) {
      setIsValidToken(false);
      setMessage("Ungültiger oder fehlender Reset-Token.");
      return;
    }

    setToken(resetToken);

    // Token-Validierung über API
    const validateToken = async () => {
      try {
        const result = await resetPasswordService.validateResetToken(
          resetToken
        );
        if (result.valid) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
          setMessage(result.message || "Token ist ungültig oder abgelaufen.");
        }
      } catch (error) {
        setIsValidToken(false);
        setMessage("Fehler bei der Token-Validierung.");
      }
    };

    validateToken();
  }, [location.search]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();

    // Passwort-Validierung
    if (passwordData.password.length < 8) {
      setMessage("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }

    if (passwordData.password !== passwordData.confirmPassword) {
      setMessage("Die Passwörter stimmen nicht überein.");
      return;
    }

    setIsProcessing(true);
    setMessage("");

    try {
      await resetPasswordService.resetPassword(token, passwordData.password);
      setMessage(
        "Ihr Passwort wurde erfolgreich zurückgesetzt. Sie werden zur Login-Seite weitergeleitet..."
      );
      setIsProcessing(false);

      // Nach 3 Sekunden zur Login-Seite weiterleiten
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (error) {
      setMessage(
        error.message ||
          "Fehler beim Zurücksetzen des Passworts. Bitte versuchen Sie es erneut."
      );
      setIsProcessing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPasswordData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear message when user starts typing
    if (message) setMessage("");
  };

  if (isValidToken === null) {
    return (
      <div className="poppins">
        <Navbar />
        <div className="w-full md:mt-28 mt-10 md:mb-20 mb-10">
          <div className="project-container w-full h-full px-4">
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4D47C3] mx-auto mb-4"></div>
                <p className="text-lg">Token wird validiert...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="poppins">
        <Navbar />
        <div className="w-full md:mt-28 mt-10 md:mb-20 mb-10">
          <div className="project-container w-full h-full px-4">
            <div className="flex justify-center items-center min-h-[400px]">
              <div className="text-center max-w-md">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h2 className="text-2xl font-semibold text-red-800 mb-4">
                    Ungültiger Link
                  </h2>
                  <p className="text-red-600 mb-6">
                    Dieser Link zum Zurücksetzen des Passworts ist ungültig oder
                    abgelaufen.
                  </p>
                  <button
                    onClick={() => navigate("/login")}
                    className="bg-[#4D47C3] text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-all duration-300"
                  >
                    Zurück zum Login
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="poppins">
      <Navbar />
      <div className="w-full md:mt-28 mt-10 md:mb-20 mb-10">
        <div className="project-container w-full h-full px-4">
          <div className="md:flex justify-center items-center">
            <div className="w-full lg:w-8/12">
              <h2 className="text-3xl md:text-4xl mb-8 lg:text-[50px] lg:text-start text-center font-semibold">
                Neues Passwort festlegen
              </h2>
              <h4 className="text-2xl lg:text-[35px] lg:text-start text-center font-medium mb-10 lg:mb-16">
                Wählen Sie ein sicheres neues Passwort für Ihr Konto.
              </h4>
              <p className="text-black mb-2 lg:text-start text-center text-sm md:text-base">
                Ihr Passwort sollte mindestens 8 Zeichen lang sein und eine
                Kombination aus Buchstaben, Zahlen und Sonderzeichen enthalten.
              </p>
            </div>

            <div className="w-full lg:w-4/12 md:mt-0 mt-10">
              <h2 className="text-3xl font-medium lg:mb-6 lg:text-start text-center">
                Neues Passwort
              </h2>

              {message && (
                <div
                  className={`mt-4 text-center p-3 rounded-lg ${
                    message.includes("erfolgreich")
                      ? "text-green-600 bg-green-50 border border-green-200"
                      : message.includes("Fehler") ||
                        message.includes("Ungültiger")
                      ? "text-red-600 bg-red-50 border border-red-200"
                      : "text-orange-600 bg-orange-50 border border-orange-200"
                  }`}
                >
                  {message}
                </div>
              )}

              <form onSubmit={handlePasswordReset}>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Neues Passwort
                  </label>
                  <FormInput
                    type="password"
                    placeholder="Mindestens 8 Zeichen"
                    value={passwordData.password}
                    onChange={(e) =>
                      handleInputChange("password", e.target.value)
                    }
                    className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                    name="password"
                    required
                    minLength="8"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Passwort bestätigen
                  </label>
                  <FormInput
                    type="password"
                    placeholder="Passwort wiederholen"
                    value={passwordData.confirmPassword}
                    onChange={(e) =>
                      handleInputChange("confirmPassword", e.target.value)
                    }
                    className="w-full py-5 px-5 bg-inputBgColor rounded-[9px] focus:outline-none placeholder:text-[15px] placeholder:text-[#A7A3FF] placeholder:font-normal"
                    name="confirmPassword"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-btnBgColor text-white py-5 rounded-[9px] focus:outline-none focus:ring mt-6 shadow-logRegBtnShadow hover:bg-opacity-90 transition-all duration-300"
                  disabled={isProcessing}
                >
                  {isProcessing
                    ? "Passwort wird zurückgesetzt..."
                    : "Passwort zurücksetzen"}
                </button>
              </form>

              <p className="text-center text-[13px] text-[#B0B0B0] mt-6">
                Doch nicht zurücksetzen?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-[#4D47C3] font-semibold hover:underline"
                >
                  Zurück zum Login
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
